import React, { useState } from 'react';
import { signIn, signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Tabs,
  Tab,
  Divider,
  Link,
  CircularProgress,
} from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function LoginForm() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Sign In form
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  
  // Sign Up form
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpGivenName, setSignUpGivenName] = useState('');
  const [signUpFamilyName, setSignUpFamilyName] = useState('');
  
  // Confirmation form
  const [confirmationCode, setConfirmationCode] = useState('');
  const [emailToConfirm, setEmailToConfirm] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signIn({
        username: signInEmail,
        password: signInPassword,
      });
      // User will be redirected automatically by AuthProvider
    } catch (err: any) {
      console.error('Sign in error:', err);
      if (err.name === 'UserNotConfirmedException') {
        setError('Please confirm your email address first. Check your email for the confirmation code.');
        setEmailToConfirm(signInEmail);
        setShowConfirmation(true);
      } else if (err.name === 'NotAuthorizedException') {
        setError('Invalid email or password. Please try again.');
      } else if (err.name === 'UserNotFoundException') {
        setError('No account found with this email address.');
      } else {
        setError(err.message || 'An error occurred during sign in.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signUp({
        username: signUpEmail,
        password: signUpPassword,
        options: {
          userAttributes: {
            email: signUpEmail,
            given_name: signUpGivenName,
            family_name: signUpFamilyName,
          },
        },
      });
      
      setSuccess('Account created! Please check your email for a confirmation code.');
      setEmailToConfirm(signUpEmail);
      setShowConfirmation(true);
    } catch (err: any) {
      console.error('Sign up error:', err);
      if (err.name === 'UsernameExistsException') {
        setError('An account with this email already exists.');
      } else if (err.name === 'InvalidPasswordException') {
        setError('Password must be at least 8 characters with uppercase, lowercase, and numbers.');
      } else {
        setError(err.message || 'An error occurred during sign up.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await confirmSignUp({
        username: emailToConfirm,
        confirmationCode: confirmationCode,
      });
      
      setSuccess('Email confirmed! You can now sign in.');
      setShowConfirmation(false);
      setTabValue(0); // Switch to sign in tab
    } catch (err: any) {
      console.error('Confirmation error:', err);
      if (err.name === 'CodeMismatchException') {
        setError('Invalid confirmation code. Please try again.');
      } else if (err.name === 'ExpiredCodeException') {
        setError('Confirmation code has expired. Please request a new one.');
      } else {
        setError(err.message || 'An error occurred during confirmation.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');

    try {
      await resendSignUpCode({ username: emailToConfirm });
      setSuccess('New confirmation code sent to your email.');
    } catch (err: any) {
      console.error('Resend code error:', err);
      setError(err.message || 'Failed to resend confirmation code.');
    } finally {
      setLoading(false);
    }
  };

  if (showConfirmation) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h5" align="center" gutterBottom>
            Confirm Your Email
          </Typography>
          
          <Typography variant="body2" align="center" color="text.secondary" paragraph>
            We sent a confirmation code to {emailToConfirm}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          <form onSubmit={handleConfirmSignUp}>
            <TextField
              fullWidth
              label="Confirmation Code"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              margin="normal"
              required
              autoFocus
              placeholder="Enter 6-digit code"
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Confirming...' : 'Confirm Email'}
            </Button>
          </form>
          
          <Box textAlign="center">
            <Link
              component="button"
              variant="body2"
              onClick={handleResendCode}
              disabled={loading}
            >
              Resend confirmation code
            </Link>
          </Box>
          
          <Box textAlign="center" sx={{ mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setShowConfirmation(false)}
            >
              Back to sign in
            </Link>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 450, width: '100%' }}>
        <Typography variant="h4" align="center" gutterBottom>
          Financial Overdue System
        </Typography>
        
        <Typography variant="body2" align="center" color="text.secondary" paragraph>
          Multi-Agent Overdue Processing Dashboard
        </Typography>
        
        {/* Demo Admin Credentials */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Demo Admin Account:</strong><br />
            Email: admin@financial-overdue.com<br />
            Password: TempPass123!<br />
            <em>(You'll be prompted to change password on first login)</em>
          </Typography>
        </Alert>
        
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label="Sign In" />
          <Tab label="Sign Up" />
        </Tabs>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
        
        <TabPanel value={tabValue} index={0}>
          <form onSubmit={handleSignIn}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={signInEmail}
              onChange={(e) => setSignInEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
            />
            
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={signInPassword}
              onChange={(e) => setSignInPassword(e.target.value)}
              margin="normal"
              required
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <form onSubmit={handleSignUp}>
            <TextField
              fullWidth
              label="First Name"
              value={signUpGivenName}
              onChange={(e) => setSignUpGivenName(e.target.value)}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Last Name"
              value={signUpFamilyName}
              onChange={(e) => setSignUpFamilyName(e.target.value)}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={signUpEmail}
              onChange={(e) => setSignUpEmail(e.target.value)}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={signUpPassword}
              onChange={(e) => setSignUpPassword(e.target.value)}
              margin="normal"
              required
              helperText="At least 8 characters with uppercase, lowercase, and numbers"
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </form>
        </TabPanel>
      </Paper>
    </Box>
  );
}